import { registerAdminAccountWithEmail } from "../common/firebase/AuthService"
import { addAdminAccount, fetchAdminAccountByEmailAndUsername, fetchAdminAccountByUsername, fetchAllAdminAccountsOrderByCreatedAt, fetchLatestEmailAuth, verifyEmailAuth, updateAdminAccountSuperUser } from "../common/firebase/Firestore"
import { authStore } from "../store/AuthStore"
import AdminAccount from "../model/auth/AdminAccount"
import isFirebaseError from "../common/firebase/FirebaseError";
import { changePassword, deleteAdminAccount, sendAuthenticationMail } from "../common/firebase/Function";
import { v4 as uuidv4 } from "uuid";
import { FirebaseError } from "firebase/app";

/**
 * 회원 등록 - 이메일 / 비밀번호 타입
 * @async
 * @function getAllAdminAccounts
 * @param {AdminAccount} adminAccount - 관리자 계정 정보 (입력 form)
 * @param {string} password - 비밀번호
 * @returns
 * @throws {Error}
 */
const getAllAdminAccounts = async (): Promise<AdminAccount[]> => {
    return await fetchAllAdminAccountsOrderByCreatedAt();
};

const requestDeleteAdminAccount = async (email: string): Promise<void> => {
    await deleteAdminAccount(email);
}

/**
 * 회원 등록 - 이메일 / 비밀번호 타입
 * @async
 * @function registerEmailUser
 * @param {AdminAccount} adminAccount - 관리자 계정 정보 (입력 form)
 * @param {string} password - 비밀번호
 * @returns
 * @throws {Error}
 */
const registerEmailUser = async (adminAccount: AdminAccount, password: string) => {
    try {
        await registerAdminAccountWithEmail(adminAccount, password);
        await addAdminAccount(adminAccount);
    } catch (error: any) {
        if (isFirebaseError(error)) {
            if (error.code === "auth/email-already-in-use") {
                throw new Error("이미 등록된 이메일입니다. 가입이 불가능합니다.");
            }
        }

        // Firebase REST API의 EMAIL_EXISTS 에러 처리
        if (error?.error?.code === 400 && error?.error?.message === "EMAIL_EXISTS") {
            throw new Error("이미 등록된 이메일입니다. 다른 이메일을 사용해주세요.");
        }

        console.error("Authentication 회원 등록 오류 발생. 에러: ", error);
        throw error;
    }
};

const checkDuplicatedUsername = async (username: string): Promise<boolean> => {
    try {
        const adminAccount = await fetchAdminAccountByUsername(username);
        return adminAccount !== null;
    } catch (error) {
        console.error("Username 중복 체크 실패");
        throw error;
    }
}

const requestAuthenticationEmail = async (email: string, username: string, needsAccountCheck: boolean): Promise<boolean> => {
    if (needsAccountCheck) {
        const adminAccount = await fetchAdminAccountByEmailAndUsername(email, username);
        
        if (adminAccount) {
            await sendAuthenticationMail(email);
            return true;
        } else {
            return false;
        }
    } else {
        await sendAuthenticationMail(email);
        return true;
    }
}

/**
 * 이메일 인증번호 검증
 * @async
 * @function validateAuthCode
 * @param {string} email - 이메일
 * @param {string} authCode - 인증번호
 * @returns {Promise<boolean>}
 */
const validateAuthCode = async (email: string, authCode: string): Promise<boolean> => {
    const latestEmailAuth = await fetchLatestEmailAuth(email);

    if (!latestEmailAuth) {
        return false;
    }

    const now = new Date();
    const isNotExpired = now <= latestEmailAuth.expiredAt;
    const isAuthCodeMatch = latestEmailAuth.authCode === authCode;
    /// 기인증 완료 여부
    const isVerified = latestEmailAuth.verified;

    if (isNotExpired && isAuthCodeMatch && !isVerified) {
        const verificationId = uuidv4();
        await verifyEmailAuth(latestEmailAuth.id, verificationId);
        /// 비밀번호 변경을 위한 임시 인증 저장
        authStore.saveChangePasswordVerificationId(verificationId);
        
        return true;
    } else {
        return false;
    }
}

/// ID / Password를 알고있는 상태에서의 비밀번호 변경
const requestChangePassword = async (email: string, oldPassword: string, newPassword: string): Promise<void> => {
    await changePassword(email, oldPassword, newPassword);
}

/// 슈퍼 관리자 권한 변경
const requestToggleSuperUser = async (email: string, username: string, isSuperUser: boolean): Promise<void> => {
    await updateAdminAccountSuperUser(email, username, isSuperUser);
}

export { getAllAdminAccounts, requestDeleteAdminAccount, registerEmailUser, checkDuplicatedUsername, requestAuthenticationEmail, validateAuthCode, requestChangePassword, requestToggleSuperUser };